import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
} from "recharts";

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { trpc } from "@/utils/trpc";

export function WeightProgressionChart() {
  const [selectedExerciseId, setSelectedExerciseId] = useState<string | null>(
    null
  );

  const exercisesQuery = useQuery(
    trpc.analytics.getLoggedExercises.queryOptions()
  );

  const progressQuery = useQuery({
    ...trpc.analytics.getExerciseProgress.queryOptions({
      exerciseId: selectedExerciseId ?? "",
      days: 30,
    }),
    enabled: !!selectedExerciseId,
  });

  const exercises = exercisesQuery.data ?? [];

  // Auto-select first exercise when data loads
  if (exercises.length > 0 && !selectedExerciseId) {
    setSelectedExerciseId(exercises[0].id);
  }

  const selectedExercise = exercises.find((e) => e.id === selectedExerciseId);
  const isInverted = !!selectedExercise?.invertedWeight;

  const chartData = (progressQuery.data ?? []).map((d) => ({
    date: formatShortDate(d.date),
    weight: d.topWeight,
    reps: d.reps,
  }));

  // Calculate nice Y-axis ticks in increments of 5 or 10
  const yAxisConfig = calculateYAxisTicks(chartData.map((d) => d.weight));

  return (
    <Card className="mb-6">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between gap-4">
          <div>
            <CardTitle className="text-lg">Weight Progression</CardTitle>
            {isInverted && (
              <p className="text-xs text-muted-foreground mt-1">
                Assisted exercise -- lower weight = less assistance (progress)
              </p>
            )}
          </div>
          <Select
            value={selectedExerciseId ?? ""}
            onValueChange={setSelectedExerciseId}
            disabled={exercisesQuery.isLoading}
          >
            <SelectTrigger className="w-[200px]">
              <SelectValue placeholder="Select exercise">
                {selectedExercise?.name ?? "Select exercise"}
              </SelectValue>
            </SelectTrigger>
            <SelectContent>
              {exercises.map((exercise) => (
                <SelectItem key={exercise.id} value={exercise.id}>
                  {exercise.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </CardHeader>
      <CardContent>
        {exercisesQuery.isLoading && (
          <div className="h-[200px] flex items-center justify-center">
            <p className="text-muted-foreground">Loading exercises...</p>
          </div>
        )}

        {exercises.length === 0 && !exercisesQuery.isLoading && (
          <div className="h-[200px] flex items-center justify-center">
            <p className="text-muted-foreground">
              No exercises logged yet. Complete a workout to see progress.
            </p>
          </div>
        )}

        {selectedExerciseId && progressQuery.isLoading && (
          <div className="h-[200px] flex items-center justify-center">
            <p className="text-muted-foreground">Loading chart...</p>
          </div>
        )}

        {selectedExerciseId && chartData.length === 0 && !progressQuery.isLoading && (
          <div className="h-[200px] flex items-center justify-center">
            <p className="text-muted-foreground">
              No data in the last 30 days for this exercise.
            </p>
          </div>
        )}

        {chartData.length > 0 && (
          <div className="h-[200px]">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={chartData}>
                <XAxis
                  dataKey="date"
                  tick={{ fontSize: 12, fill: "#a1a1aa" }}
                  tickLine={false}
                  axisLine={false}
                />
                <YAxis
                  tick={{ fontSize: 12, fill: "#a1a1aa" }}
                  tickLine={false}
                  axisLine={false}
                  domain={[yAxisConfig.min, yAxisConfig.max]}
                  ticks={yAxisConfig.ticks}
                  tickFormatter={(value) => `${value}lb${isInverted ? " assist" : ""}`}
                />
                <Tooltip
                  content={({ active, payload }) => {
                    if (!active || !payload?.length) return null;
                    const data = payload[0].payload;
                    return (
                      <div className="bg-popover border rounded-lg shadow-lg px-3 py-2">
                        <p className="font-medium">{data.date}</p>
                        <p className="text-sm text-muted-foreground">
                          {data.weight} lbs{isInverted ? " assist" : ""} x {data.reps} reps
                        </p>
                      </div>
                    );
                  }}
                />
                <Line
                  type="monotone"
                  dataKey="weight"
                  stroke="#3b82f6"
                  strokeWidth={2}
                  dot={{ fill: "#3b82f6", r: 4 }}
                  activeDot={{ fill: "#60a5fa", r: 6, stroke: "#1e3a5f", strokeWidth: 2 }}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function formatShortDate(dateStr: string): string {
  const date = new Date(dateStr);
  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
  });
}

function calculateYAxisTicks(weights: number[]): {
  min: number;
  max: number;
  ticks: number[];
} {
  if (weights.length === 0) {
    return { min: 0, max: 100, ticks: [0, 25, 50, 75, 100] };
  }

  const minWeight = Math.min(...weights);
  const maxWeight = Math.max(...weights);
  const range = maxWeight - minWeight;

  // Determine increment: 5 for small ranges, 10 for larger
  const increment = range <= 30 ? 5 : 10;

  // Round min down and max up to nearest increment
  const min = Math.floor(minWeight / increment) * increment - increment;
  const max = Math.ceil(maxWeight / increment) * increment + increment;

  // Generate ticks
  const ticks: number[] = [];
  for (let tick = min; tick <= max; tick += increment) {
    ticks.push(tick);
  }

  // Limit to ~5 ticks for readability
  if (ticks.length > 6) {
    const step = Math.ceil(ticks.length / 5);
    const filteredTicks = ticks.filter((_, i) => i % step === 0);
    // Always include the last tick
    if (filteredTicks[filteredTicks.length - 1] !== ticks[ticks.length - 1]) {
      filteredTicks.push(ticks[ticks.length - 1]!);
    }
    return { min, max, ticks: filteredTicks };
  }

  return { min, max, ticks };
}
